<template>
    <component :is="linkComponent" v-bind="linkProps" class="fia-card" :class="`accent-${accentIndex % 5}`">
        <div class="fia-card-top">
            <div class="fia-card-icon">
                <span v-html="cardIcon"></span>
            </div>
            <div class="fia-card-body">
                <h3 class="fia-card-title">{{ item.title }}</h3>
                <p class="fia-card-desc" v-if="item.subtitle">{{ item.subtitle }}</p>
            </div>
        </div>

        <div class="fia-card-footer">
            <span class="fia-card-link">{{ $t('preview') || 'Pregledaj' }} →</span>
        </div>
    </component>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    name: 'FiaIntelligenceCard',

    props: {
        item: {
            type: Object,
            required: true
        },
        accentIndex: {
            type: Number,
            default: 0
        }
    },

    computed: {
        ...mapGetters(['getContentTypes']),

        iconKey() {
            return (this.item.slug || this.item.title || '')
                .toLowerCase()
                .trim()
                .replace(/[čć]/g, 'c')
                .replace(/š/g, 's')
                .replace(/đ/g, 'd')
                .replace(/ž/g, 'z')
                .replace(/\s+/g, '-')
        },

        cardIcon() {
            const icons = {
                // Knowledge Hub children
                'fia-economic-reports': `<i class="fa fa-bar-chart"></i>`,
                'economic-reports': `<i class="fa fa-bar-chart"></i>`,
                'sectoral-reviews': `<i class="fa fa-pie-chart"></i>`,
                'annual-business-reports': `<i class="fa fa-file-text-o"></i>`,
                'research-papers': `<i class="fa fa-search"></i>`,
                'policy-briefs': `<i class="fa fa-file-o"></i>`,
                'data-compendiums': `<i class="fa fa-database"></i>`,
                // Insights children
                'fia-business-pulse': `<i class="fa fa-heartbeat"></i>`,
                'business-pulse': `<i class="fa fa-heartbeat"></i>`,
                'distress-index': `<i class="fa fa-warning"></i>`,
                'market-insights': `<i class="fa fa-line-chart"></i>`,
                'sve-infografike': `<i class="fa fa-line-chart"></i>`,
                'all-infographics': `<i class="fa fa-line-chart"></i>`,
                'financial-analysis': `<i class="fa fa-calculator"></i>`,
                'economic-briefs': `<i class="fa fa-file-text-o"></i>`,
                'data-products': `<i class="fa fa-database"></i>`,
                'custom-reports': `<i class="fa fa-files-o"></i>`,
                'api-data-access': `<i class="fa fa-code"></i>`,
                'finansijski-pregled-za-neprofitne-organizacije': `<i class="fa fa-handshake-o"></i>`,
                'financial-overview-of-nonprofit-organizations': `<i class="fa fa-handshake-o"></i>`,
                'fia-insights': `<i class="fa fa-area-chart"></i>`,
                // Heritage children
                'heritage': `<i class="fa fa-university"></i>`,
                'archive': `<i class="fa fa-archive"></i>`,
                'history': `<i class="fa fa-clock-o"></i>`,
                // Gateway
                'gateway': `<i class="fa fa-credit-card"></i>`,
            }

            if (icons[this.iconKey]) return icons[this.iconKey]

            // Partial match fallback
            for (const key of Object.keys(icons)) {
                if (this.iconKey.includes(key) || key.includes(this.iconKey)) {
                    return icons[key]
                }
            }

            // Generic fallbacks cycling through accent palette
            const fallbacks = [
                `<i class="fa fa-bar-chart"></i>`,
                `<i class="fa fa-file-text-o"></i>`,
                `<i class="fa fa-search"></i>`,
                `<i class="fa fa-database"></i>`,
                `<i class="fa fa-line-chart"></i>`,
            ]
            return fallbacks[this.accentIndex % fallbacks.length]
        },

        isExternalLink() {
            return this.item.url && this.item.url.startsWith('http')
        },

        isInternalLink() {
            return this.item.url && !this.item.url.startsWith('http')
        },

        isInfographic() {
            if (!this.item.tags) return false
            try {
                return JSON.parse(this.item.tags).some(t => t.tagId === this.$store.state.tagIds.infographic)
            } catch { return false }
        },

        linkComponent() {
            return (this.isExternalLink && !this.isInfographic) ? 'a' : 'router-link'
        },

        linkProps() {
            if (this.isInfographic) {
                return { to: { name: 'news-content', params: { id: this.item.id } } }
            }
            if (this.isExternalLink) {
                return { href: this.item.url, target: '_blank' }
            }
            if (this.isInternalLink) {
                return { to: { name: this.item.url } }
            }
            return {
                to: {
                    name: 'fiaIntelligenceDetails',
                    params: { id: this.item.id, slug: this.item.slug }
                }
            }
        }
    }
}
</script>

<style scoped>
.fia-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 22px 20px 18px;
    border-radius: 12px;
    background: #fafcff;
    border: 1px solid #e8eef6;
    text-decoration: none;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
    min-height: 160px;
    cursor: pointer;
}

.fia-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.09);
    text-decoration: none;
}

/* ── Top row ── */
.fia-card-top {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 14px;
}

.fia-card-icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
}

/* ── Accent colours ── */
.accent-0 .fia-card-icon { background: #dbeafe; color: #1d4ed8; }
.accent-0:hover           { border-color: #bfdbfe; }

.accent-1 .fia-card-icon { background: #ffedd5; color: #ea580c; }
.accent-1:hover           { border-color: #fed7aa; }

.accent-2 .fia-card-icon { background: #dcfce7; color: #16a34a; }
.accent-2:hover           { border-color: #bbf7d0; }

.accent-3 .fia-card-icon { background: #ede9fe; color: #7c3aed; }
.accent-3:hover           { border-color: #ddd6fe; }

.accent-4 .fia-card-icon { background: #fce7f3; color: #be185d; }
.accent-4:hover           { border-color: #fbcfe8; }

/* ── Body text ── */
.fia-card-body {
    flex: 1;
}

.fia-card-title {
    font-size: 17px;
    font-weight: 700;
    color: #1e3a5f;
    margin: 0 0 6px;
    line-height: 1.3;
}

.fia-card-desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
    line-height: 1.55;
}

/* ── Footer link ── */
.fia-card-footer {
    padding-top: 12px;
    border-top: 1px solid #f1f5f9;
    margin-top: auto;
}

.fia-card-link {
    font-size: 13px;
    font-weight: 600;
    color: #ea580c;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: transform 0.2s ease, color 0.2s ease;
}

.fia-card:hover .fia-card-link {
    transform: translateX(3px);
    color: #1d4ed8;
}
</style>